import type { BalanceSheetReconciliationJson, ReportRequestBody } from '@query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { ReconciliationAccountEntriesPanel } from './ReconciliationAccountEntriesPanel'
import {
  AccountCode,
  Caret,
  ColumnHeaderRow,
  InlineAccountCode,
  LineItemHeader,
  ReportRow,
  RowLabel,
  RowLabelText,
  RowMeta
} from './interactive.styled'
import { specificationsTableMinWidthPx } from './periodUtils'
import { ReportCardLayout } from './ReportCardLayout'
import { ReportLeadingGutter } from './ReportLeadingGutter'
import type { ReportExpansion } from './interactiveReportsSession'
import { useOnTokenIncrement } from './useOnTokenIncrement'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  data: BalanceSheetReconciliationJson
  expansion: ReportExpansion
  onExpansionChange: (patch: Partial<ReportExpansion>) => void
  expandAllToken: number
  collapseAllToken: number
  currency?: string
  isRefreshing?: boolean
  isStale?: boolean
}

/**
 * Section names repeat across the balance sheet template at the same level, so the
 * row position is the only stable identity for a row and its accounts.
 */
const rowKey = (rowIndex: number, sectionName: string) => `${rowIndex}::${sectionName}`

const accountKey = (rowIndex: number, accountNumber: string) => `${rowIndex}::${accountNumber}`

const entryCount = (count?: number, entries?: unknown[]) => count ?? entries?.length ?? 0

export const SpecificationsView: React.FC<Props> = ({
  slug,
  reportBody,
  data,
  expansion,
  onExpansionChange,
  expandAllToken,
  collapseAllToken,
  currency,
  isRefreshing = false,
  isStale = false
}) => {
  const [t] = useTranslation()
  const expanded = expansion.items

  const expandableKeys = useMemo(
    () =>
      data.rows.flatMap((row, rowIndex) =>
        row.accounts
          .filter(account => entryCount(account.entries_count, account.entries) > 0)
          .map(account => accountKey(rowIndex, account.number))
      ),
    [data.rows]
  )

  useOnTokenIncrement(expandAllToken, () => {
    const next: Record<string, boolean> = {}
    expandableKeys.forEach(key => {
      next[key] = true
    })
    onExpansionChange({ items: next })
  })

  useOnTokenIncrement(collapseAllToken, () => {
    onExpansionChange({ items: {} })
  })

  return (
    <ReportCardLayout
      title={t('constants.reportType.balanceSheetSpecifications')}
      isRefreshing={isRefreshing}
      isStale={isStale}
      footNote={t('reports.interactive.footNote.specifications')}
      tableMinWidth={specificationsTableMinWidthPx()}
    >
      <ColumnHeaderRow>
        <ReportLeadingGutter />
        <LineItemHeader>{t('reports.interactive.account')}</LineItemHeader>
      </ColumnHeaderRow>

      {data.rows.map((row, rowIndex) => (
        <React.Fragment key={rowKey(rowIndex, row.name)}>
          <ReportRow $strong={row.level === 0} $borderStrong={row.level === 0}>
            <AccountCode />
            <RowLabel $indentFrom={16 + row.level * 12} $strong={row.level === 0}>
              <Caret />
              <RowLabelText>{row.name}</RowLabelText>
            </RowLabel>
          </ReportRow>

          {row.accounts.map(account => {
            const key = accountKey(rowIndex, account.number)
            const count = entryCount(account.entries_count, account.entries)
            const hasEntries = count > 0
            const open = !!expanded[key]

            return (
              <React.Fragment key={key}>
                <ReportRow
                  $clickable={hasEntries}
                  $bg={open ? '#f7fafd' : undefined}
                  onClick={() =>
                    hasEntries &&
                    onExpansionChange({ items: { ...expanded, [key]: !expanded[key] } })
                  }
                >
                  <AccountCode />
                  <RowLabel $indentFrom={16 + row.level * 12 + 20}>
                    <Caret>
                      {hasEntries ? open ? <FaChevronDown /> : <FaChevronRight /> : null}
                    </Caret>
                    <RowLabelText>
                      <InlineAccountCode>{account.number}</InlineAccountCode>
                      {account.name}
                      {hasEntries && (
                        <RowMeta>{t('reports.interactive.entryCount', { count })}</RowMeta>
                      )}
                    </RowLabelText>
                  </RowLabel>
                </ReportRow>

                {open && hasEntries && (
                  <ReconciliationAccountEntriesPanel
                    slug={slug}
                    reportBody={reportBody}
                    accountNumber={account.number}
                    currency={currency}
                  />
                )}
              </React.Fragment>
            )
          })}
        </React.Fragment>
      ))}
    </ReportCardLayout>
  )
}
